import * as Phaser from "phaser"
import { EventBus } from "../core/event-bus"
import { GameState } from "../core/game-state"
import { getHeroColorTheme } from "../core/hero-colors"
import { EVENT, SCENE, TEX } from "../core/keys"
import { setMusicMenuVolume } from "../core/music-volume"
import { THEME } from "../core/theme"
import { GAME_HEIGHT, GAME_WIDTH, MAX_WAVES } from "../data/constants"
import type { BumperEffectType, RunStage } from "../types"
import { RetroBar, RetroPanel } from "../ui/components"
import { NEON, drawNeonBorder } from "../ui/neon-fx"

const FONT = THEME.fonts
const COLOR = THEME.colors

const HUD_DEPTH = 1000

const C = {
  panelBg: COLOR.black,
  trackBg: COLOR.black,
  hpHigh: COLOR.danger,
  hpMid: COLOR.orangeSoft,
  hpLow: COLOR.dangerDeep,
  waveBarOpening: COLOR.success,
  waveBarPressure: COLOR.warning,
  waveBarClimax: COLOR.danger,
  bossBar: COLOR.danger,
} as const

const L = {
  margin: 12,
  panelRadius: 6,
  statusW: 180,
  statusH: 74,
  wavePanelW: 140,
  wavePanelH: 52,
  barHeight: 14,
  barHeightSmall: 6,
  barRadius: 2,
  progressBarH: 4,
  bracketSize: 32,
} as const

interface BossPartSnapshot {
  id: string
  name: string
  hp: number
  maxHp: number
}

export class HudScene extends Phaser.Scene {
  // ── Cockpit Framing ──────────────────────────────────────────
  private cockpitBrackets!: Phaser.GameObjects.Graphics

  // ── Status panel (top-left) ───────────────────────────────────
  private statusContainer!: Phaser.GameObjects.Container
  private playerNameLabel!: Phaser.GameObjects.Text
  private hpBar!: RetroBar
  private hpLabel!: Phaser.GameObjects.Text
  private levelLabel!: Phaser.GameObjects.Text
  private expBar!: RetroBar
  private coinsLabel!: Phaser.GameObjects.Text

  // ── Wave panel (top-right, instead of center) ─────────────────
  private waveContainer!: Phaser.GameObjects.Container
  private waveLabel!: Phaser.GameObjects.Text
  private waveTimerLabel!: Phaser.GameObjects.Text
  private waveScoreLabel!: Phaser.GameObjects.Text

  // ── Bumper Tip (top-right) ───────────────────────────────────
  private bumperTipContainer?: Phaser.GameObjects.Container
  private bumperTipText?: Phaser.GameObjects.Text
  private bumperTipTween?: Phaser.Time.TimerEvent | Phaser.Tweens.Tween

  // ── Combo display ──────────────────────────────────────────────
  private comboContainer!: Phaser.GameObjects.Container
  private comboBackdrop!: Phaser.GameObjects.Graphics
  private comboPips: Phaser.GameObjects.Graphics[] = []
  private comboTimerBar!: Phaser.GameObjects.Graphics
  private comboStateLabel!: Phaser.GameObjects.Text
  private comboIsPowered = false
  private comboPulseStrength = 0

  // ── Sector name display ───────────────────────────────────────
  private sectorNameLabel!: Phaser.GameObjects.Text

  // ── Overdrive Tap Urgency ──────────────────────────────────────
  private tapUrgencyContainer?: Phaser.GameObjects.Container
  private tapUrgencyText?: Phaser.GameObjects.Text
  private tapUrgencySubtext?: Phaser.GameObjects.Text
  private tapUrgencyShowCount = 0

  // ── Asset warning ─────────────────────────────────────────────
  private warningLabel?: Phaser.GameObjects.Text
  private warningKeys: string[] = []
  private debugCounterText?: Phaser.GameObjects.Text

  // ── Dirty-check cache ─────────────────────────────────────────
  private hpGhostPct = 1
  private lastHpPct = 1
  private prevHudRunState = "" as RunStage | ""
  private prev = {
    hp: -1,
    maxHp: -1,
    level: -1,
    exp: -1,
    expToLevel: -1,
    coins: -1,
    wave: -1,
    isBossWave: false,
    waveTimerSec: -1,
    score: -1,
    comboState: "" as string,
    comboHits: -1,
    comboTargetHits: -1,
    comboNextEffect: "" as string,
    comboHitColors: [] as string[],
    powerMode: "" as string,
    powerTimerPct: -1,
    runState: "" as RunStage | "",
    sectorName: "" as string,
    bossName: "" as string,
    bossHp: -1,
    bossMaxHp: -1,
    bossPartCount: -1,
    bossType: "" as string,
    bossParts: [] as BossPartSnapshot[],
    isBossMainBarVisible: true,
  }

  constructor() {
    super({ key: SCENE.HUD })
  }

  create(): void {
    this.hpGhostPct = 1
    this.lastHpPct = 1
    this.prevHudRunState = ""
    this.prev = {
      hp: -1,
      maxHp: -1,
      level: -1,
      exp: -1,
      expToLevel: -1,
      coins: -1,
      wave: -1,
      isBossWave: false,
      waveTimerSec: -1,
      score: -1,
      comboState: "" as string,
      comboHits: -1,
      comboTargetHits: -1,
      comboNextEffect: "" as string,
      comboHitColors: [],
      powerMode: "" as string,
      powerTimerPct: -1,
      runState: "" as RunStage | "",
      sectorName: "" as string,
      bossName: "" as string,
      bossHp: -1,
      bossMaxHp: -1,
      bossPartCount: -1,
      bossType: "" as string,
      bossParts: [] as BossPartSnapshot[],
      isBossMainBarVisible: true,
    }
    this.warningKeys = []
    this.comboPips = []
    this.comboIsPowered = false
    this.comboPulseStrength = 0
    this.warningLabel = undefined
    this.debugCounterText = undefined
    this.tapUrgencyShowCount = 0

    this.createHudLayout()
    this.registerHudEvents()
    this.createDebugCounter()
    this.onRegistryChange()
    this.events.once("shutdown", () => this.onShutdown())
  }

  update(): void {
    this.updateDebugCounter()
    this.updateComboPulse()

    // Smoothly lerp ghost salvageth toward actual salvageth
    if (this.hpGhostPct > this.lastHpPct) {
      this.hpGhostPct = Phaser.Math.Linear(
        this.hpGhostPct,
        this.lastHpPct,
        0.05,
      )
      if (this.hpGhostPct - this.lastHpPct < 0.001)
        this.hpGhostPct = this.lastHpPct
      this.redrawHpBar(this.prev.hp, this.prev.maxHp)
    } else if (this.hpGhostPct < this.lastHpPct) {
      this.hpGhostPct = this.lastHpPct
      this.redrawHpBar(this.prev.hp, this.prev.maxHp)
    }
  }

  // ── Boss health bar ────────────────────────────────────────────
  private bossBarContainer?: Phaser.GameObjects.Container
  private bossBarFill?: Phaser.GameObjects.Graphics
  private bossBarLabel?: Phaser.GameObjects.Text
  private bossHpLabel?: Phaser.GameObjects.Text

  private genericPartsContainer?: Phaser.GameObjects.Container
  private partBars: Map<
    string,
    {
      fill: Phaser.GameObjects.Graphics
      label: Phaser.GameObjects.Text
      hpLabel: Phaser.GameObjects.Text
      bg: Phaser.GameObjects.Graphics
    }
  > = new Map()

  private createHudLayout(): void {
    this.createCockpitFraming()
    this.createStatusPanel()
    this.createWavePanel()
    this.createBumperTipArea()
    this.createComboDisplay()
    this.createSectorNameLabel()
    this.createBossBar()
    this.createGenericPartsBars()
    this.createTapUrgencyDisplay()
  }

  private createGenericPartsBars(): void {
    const x = GAME_WIDTH - 120 // Bottom-right alignment
    const y = GAME_HEIGHT - 60

    this.genericPartsContainer = this.add
      .container(x, y)
      .setScrollFactor(0)
      .setDepth(HUD_DEPTH)
      .setVisible(false)
  }

  private createBossBar(): void {
    const x = GAME_WIDTH / 2
    const y = GAME_HEIGHT - 90
    const w = 400
    const h = 12

    this.bossBarContainer = this.add
      .container(x, y)
      .setScrollFactor(0)
      .setDepth(HUD_DEPTH)
      .setVisible(false)

    const bg = this.add.graphics()
    bg.fillStyle(C.panelBg, 0.6)
    bg.fillRoundedRect(-w / 2, 0, w, h, 2)
    drawNeonBorder(bg, -w / 2, 0, w, h, NEON.red, 2, 0.3, 0.1)
    this.bossBarContainer.add(bg)

    this.bossBarFill = this.add.graphics()
    this.bossBarContainer.add(this.bossBarFill)

    this.bossBarLabel = this.add
      .text(0, -14, "BOSS", {
        fontSize: "8px",
        fontFamily: FONT.pixel,
        color: COLOR.dangerText,
        letterSpacing: 2,
      })
      .setOrigin(0.5, 0)
    this.bossBarContainer.add(this.bossBarLabel)

    this.bossHpLabel = this.add
      .text(w / 2 - 4, -14, "0/0", {
        fontSize: "8px",
        fontFamily: FONT.pixel,
        color: COLOR.whiteText,
      })
      .setOrigin(1, 0)
    this.bossBarContainer.add(this.bossHpLabel)
  }

  private createTapUrgencyDisplay(): void {
    const x = GAME_WIDTH / 2
    const y = GAME_HEIGHT / 2 + 100

    this.tapUrgencyContainer = this.add
      .container(x, y)
      .setScrollFactor(0)
      .setDepth(HUD_DEPTH + 900)
      .setVisible(false)

    const heroId = GameState.instance.character?.id ?? "bella"
    const heroColors = getHeroColorTheme(heroId)

    this.tapUrgencyText = this.add
      .text(0, 0, "OVERDRIVE", {
        fontSize: "64px",
        fontFamily: FONT.pixel,
        color: "#ffffff",
        fontStyle: "bold italic",
      })
      .setOrigin(0.5)
      .setShadow(0, 0, heroColors.glowText, 20, true, true)

    this.tapUrgencySubtext = this.add
      .text(0, 50, "Bark! Fire! Fire!", {
        fontSize: "14px",
        fontFamily: FONT.pixel,
        color: heroColors.glowText,
        fontStyle: "bold",
      })
      .setOrigin(0.5)
      .setShadow(0, 0, "#000000", 4, true, true)

    this.tapUrgencyContainer.add([this.tapUrgencyText, this.tapUrgencySubtext])

    // Pulse animation
    this.tweens.add({
      targets: this.tapUrgencyContainer,
      scale: 1.1,
      duration: 150,
      yoyo: true,
      repeat: -1,
      ease: "Sine.easeInOut",
    })

    // Text color flicker
    this.tweens.add({
      targets: this.tapUrgencyText,
      alpha: 0.7,
      duration: 50,
      yoyo: true,
      repeat: -1,
      ease: "Steps(1)",
    })
  }

  private redrawBossBar(hp: number, maxHp: number): void {
    if (!this.bossBarFill) return
    this.bossBarFill.clear()

    const w = 396
    const h = 8
    const pct = Math.max(0, Math.min(1, hp / maxHp))
    const fillW = w * pct

    this.bossBarFill.fillStyle(COLOR.danger, 0.8)
    this.bossBarFill.fillRect(-w / 2 + 2, 2, fillW, h)

    this.bossBarFill.fillStyle(COLOR.white, 0.2)
    this.bossBarFill.fillRect(-w / 2 + 2, 2, fillW, 2)

    if (this.bossHpLabel) {
      this.bossHpLabel.setText(`${Math.ceil(hp)}/${Math.ceil(maxHp)}`)
    }
  }

  private syncBossBar(snapshot: {
    bossHp: number
    bossMaxHp: number
    bossPartCount: number
    bossName: string
    bossType: string
    bossParts: BossPartSnapshot[]
    isBossMainBarVisible: boolean
  }): void {
    const hp = snapshot.bossHp
    const maxHp = snapshot.bossMaxHp
    const partCount = snapshot.bossPartCount
    const bossName = snapshot.bossName
    const bossType = snapshot.bossType
    const isActive = maxHp > 0 && hp > 0
    const isMainVisible = isActive && snapshot.isBossMainBarVisible

    this.bossBarContainer?.setVisible(isMainVisible)

    // Generic parts handling
    const hasGenericParts = snapshot.bossParts.length > 0
    if (this.genericPartsContainer) {
      this.genericPartsContainer.setVisible(isActive && hasGenericParts)
      if (isActive && hasGenericParts) {
        this.syncGenericParts(snapshot.bossParts)
      }
    }

    if (isActive) {
      if (bossName !== this.prev.bossName) {
        this.bossBarLabel?.setText(`${bossName || "BOSS"}`)
        this.prev.bossName = bossName
      }

      if (
        hp !== this.prev.bossHp ||
        maxHp !== this.prev.bossMaxHp ||
        partCount !== this.prev.bossPartCount ||
        snapshot.isBossMainBarVisible !== this.prev.isBossMainBarVisible
      ) {
        this.redrawBossBar(hp, maxHp)
        this.prev.bossHp = hp
        this.prev.bossMaxHp = maxHp
        this.prev.bossPartCount = partCount
        this.prev.isBossMainBarVisible = snapshot.isBossMainBarVisible
      }

      this.prev.bossType = bossType
      this.prev.bossParts = [...snapshot.bossParts]
    }
  }

  private syncGenericParts(parts: BossPartSnapshot[]): void {
    if (!this.genericPartsContainer) return

    const w = 100
    const h = 6
    const spacing = 12

    // Clean up old bars that are no longer present
    const currentIds = new Set(parts.map((p) => p.id))
    for (const [id, elements] of this.partBars.entries()) {
      if (!currentIds.has(id)) {
        elements.bg.destroy()
        elements.fill.destroy()
        elements.label.destroy()
        elements.hpLabel.destroy()
        this.partBars.delete(id)
      }
    }

    // Update or create bars
    parts.forEach((part, index) => {
      const offsetY = -index * spacing
      let elements = this.partBars.get(part.id)

      if (!elements) {
        const bg = this.add.graphics()
        bg.fillStyle(C.panelBg, 0.6)
        bg.fillRoundedRect(-w / 2, 0, w, h, 2)
        this.genericPartsContainer?.add(bg)

        const fill = this.add.graphics()
        this.genericPartsContainer?.add(fill)

        const label = this.add.text(-w / 2, -8, part.name, {
          fontSize: "5px",
          fontFamily: FONT.pixel,
          color: COLOR.whiteText,
        })
        this.genericPartsContainer?.add(label)

        const hpLabel = this.add
          .text(w / 2, -8, "0/0", {
            fontSize: "5px",
            fontFamily: FONT.pixel,
            color: COLOR.whiteText,
          })
          .setOrigin(1, 0)
        this.genericPartsContainer?.add(hpLabel)

        elements = { bg, fill, label, hpLabel }
        this.partBars.set(part.id, elements)
      }

      // Update positions
      elements.bg.y = offsetY
      elements.fill.y = offsetY
      elements.label.y = offsetY - 6
      elements.hpLabel.y = offsetY - 6

      // Redraw fill
      elements.fill.clear()
      const pct = Math.max(0, Math.min(1, part.hp / part.maxHp))
      const fillW = Math.max(0, (w - 4) * pct)
      elements.fill.fillStyle(COLOR.info, 0.8)
      elements.fill.fillRect(-w / 2 + 2, 2, fillW, h - 4)

      // Update HP text
      elements.hpLabel.setText(`${Math.ceil(part.hp)}/${Math.ceil(part.maxHp)}`)
    })
  }

  private createCockpitFraming(): void {
    this.cockpitBrackets = this.add
      .graphics()
      .setScrollFactor(0)
      .setDepth(HUD_DEPTH - 1)
    const s = L.bracketSize
    const m = L.margin / 2

    this.cockpitBrackets.lineStyle(2, COLOR.cyanBright, 0.25)

    // Top-left bracket
    this.cockpitBrackets
      .moveTo(m, m + s)
      .lineTo(m, m)
      .lineTo(m + s, m)
    // Top-right bracket
    this.cockpitBrackets
      .moveTo(GAME_WIDTH - m - s, m)
      .lineTo(GAME_WIDTH - m, m)
      .lineTo(GAME_WIDTH - m, m + s)
    // Bottom-left bracket
    this.cockpitBrackets
      .moveTo(m, GAME_HEIGHT - m - s)
      .lineTo(m, GAME_HEIGHT - m)
      .lineTo(m + s, GAME_HEIGHT - m)
    // Bottom-right bracket
    this.cockpitBrackets
      .moveTo(GAME_WIDTH - m - s, GAME_HEIGHT - m)
      .lineTo(GAME_WIDTH - m, GAME_HEIGHT - m)
      .lineTo(GAME_WIDTH - m, GAME_HEIGHT - m - s)

    this.cockpitBrackets.strokePath()
  }

  private registerHudEvents(): void {
    EventBus.on(EVENT.ASSET_WARNING, this.handleAssetWarning)
    EventBus.on(EVENT.BUMPER_TIP, (data: { text: string; color: string }) => {
      this.showBumperTip(data.text, data.color)
    })
    this.game.registry.events.on("changedata", this.onRegistryChange, this)
  }

  private unregisterHudEvents(): void {
    this.game.registry.events.off("changedata", this.onRegistryChange, this)
    EventBus.off(EVENT.BUMPER_TIP)
    EventBus.off(EVENT.ASSET_WARNING, this.handleAssetWarning)
  }

  private readonly handleAssetWarning = (data: { key: string }): void => {
    this.warningKeys.push(data.key)
    this.updateWarningLabel()
  }

  // ── Wave progress bar (full-width top) ────────────────────────

  // ── Status panel (top-left) ───────────────────────────────────

  private createStatusPanel(): void {
    const x = L.margin
    const y = L.margin

    const panel = new RetroPanel(this, {
      x,
      y,
      width: L.statusW,
      height: L.statusH,
      color: 0x333344,
      alpha: 1.0,
    })
      .setScrollFactor(0)
      .setDepth(HUD_DEPTH)
    this.statusContainer = panel

    this.hpLabel = this.add
      .text(L.statusW - 10, 4, "", {
        fontSize: "8px",
        fontFamily: FONT.pixel,
        color: COLOR.whiteText,
      })
      .setOrigin(1, 0)
    this.statusContainer.add(this.hpLabel)

    // HP Bar
    this.hpBar = new RetroBar(this, {
      x: 8,
      y: 16,
      width: L.statusW - 16,
      height: L.barHeight,
      color: COLOR.danger,
    })
    this.statusContainer.add(this.hpBar)

    this.playerNameLabel = this.add.text(8, 4, GameState.instance.playerName, {
      fontSize: "10px",
      fontFamily: FONT.pixel,
      color: GameState.instance.playerColor,
    })
    this.statusContainer.add(this.playerNameLabel)

    this.levelLabel = this.add.text(8, 36, "", {
      fontSize: "8px",
      fontFamily: FONT.pixel,
      color: COLOR.whiteText,
    })
    this.statusContainer.add(this.levelLabel)

    // EXP Bar
    this.expBar = new RetroBar(this, {
      x: 48,
      y: 38,
      width: L.statusW - 56,
      height: L.barHeightSmall,
      color: COLOR.success,
    })
    this.statusContainer.add(this.expBar)

    // Coins
    const coinY = 50
    const coinIcon = this.add.sprite(18, coinY + 8, TEX.COIN_RED).setScale(0.8)
    coinIcon.play("anim_coin_l")
    this.statusContainer.add(coinIcon)

    this.coinsLabel = this.add.text(34, coinY, "0", {
      fontSize: "14px",
      fontFamily: FONT.pixel,
      color: COLOR.warningText,
    })
    this.statusContainer.add(this.coinsLabel)
  }

  private redrawHpBar(hp: number, maxHp: number): void {
    const percent = Math.max(0, Math.min(1, hp / maxHp))
    this.lastHpPct = percent

    this.hpBar.setPercent(percent)
    this.hpLabel.setText(`${Math.ceil(hp)}/${maxHp}`)
  }

  private redrawExpBar(exp: number, expToLevel: number): void {
    const percent = Math.max(0, Math.min(1, exp / Math.max(expToLevel, 1)))
    this.expBar.setPercent(percent)
  }

  // ── Wave panel (top-center) ───────────────────────────────────

  private createWavePanel(): void {
    const x = GAME_WIDTH / 2 - L.wavePanelW / 2
    const y = L.margin

    const panel = new RetroPanel(this, {
      x,
      y,
      width: L.wavePanelW,
      height: L.wavePanelH,
      color: 0x334466,
      alpha: 1.0,
    })
      .setScrollFactor(0)
      .setDepth(HUD_DEPTH)
    this.waveContainer = panel

    this.waveLabel = this.add
      .text(L.wavePanelW / 2, 6, "WAVE 1", {
        fontSize: "8px",
        fontFamily: FONT.pixel,
        color: COLOR.whiteText,
      })
      .setOrigin(0.5, 0)
    this.waveContainer.add(this.waveLabel)

    this.waveTimerLabel = this.add
      .text(L.wavePanelW / 2, 20, "0:00", {
        fontSize: "12px",
        fontFamily: FONT.pixel,
        color: COLOR.dangerText,
      })
      .setOrigin(0.5, 0)
    this.waveContainer.add(this.waveTimerLabel)

    this.waveScoreLabel = this.add
      .text(L.wavePanelW / 2, 38, "SCORE 0", {
        fontSize: "8px",
        fontFamily: FONT.pixel,
        color: COLOR.warningText,
      })
      .setOrigin(0.5, 0)
    this.waveContainer.add(this.waveScoreLabel)
  }

  // ── Bumper Tip Area (top-right) ────────────────────────────────

  private createBumperTipArea(): void {
    const x = GAME_WIDTH - L.margin - 140
    const y = L.margin + 4
    const w = 140
    const h = 40

    this.bumperTipContainer = this.add
      .container(x, y)
      .setScrollFactor(0)
      .setDepth(HUD_DEPTH)
      .setAlpha(0)

    this.bumperTipText = this.add
      .text(w / 2, h / 2, "", {
        fontSize: "9px",
        fontFamily: FONT.pixel,
        color: "#ffffff",
        align: "center",
        wordWrap: { width: w },
        stroke: "#000000",
        strokeThickness: 4,
      })
      .setOrigin(0.5)

    this.bumperTipContainer.add(this.bumperTipText)
  }

  private showBumperTip(text: string, color: string): void {
    if (!this.bumperTipContainer || !this.bumperTipText) return

    // Stop existing tweens
    if (this.bumperTipTween) {
      if (this.bumperTipTween instanceof Phaser.Tweens.Tween) {
        this.bumperTipTween.stop()
      } else {
        this.bumperTipTween.remove()
      }
    }

    this.bumperTipText.setText(text.toUpperCase())
    this.bumperTipText.setColor(color)

    this.bumperTipContainer.setAlpha(0)
    this.bumperTipContainer.y = L.margin - 10

    this.tweens.add({
      targets: this.bumperTipContainer,
      alpha: 1,
      y: L.margin + 4,
      duration: 200,
      ease: "Back.easeOut",
    })

    this.bumperTipTween = this.time.delayedCall(2500, () => {
      this.tweens.add({
        targets: this.bumperTipContainer,
        alpha: 0,
        y: L.margin - 10,
        duration: 300,
        ease: "Cubic.easeIn",
      })
    })
  }

  // ── Combo display (bottom-center) ──────────────────────────────

  private createComboDisplay(): void {
    // Position: bottom-center of screen
    const x = GAME_WIDTH / 2
    const y = GAME_HEIGHT - 50
    this.comboContainer = this.add
      .container(x, y)
      .setScrollFactor(0)
      .setDepth(HUD_DEPTH)
    this.comboContainer.setVisible(false)

    this.comboBackdrop = this.add.graphics()
    this.comboContainer.add(this.comboBackdrop)

    for (let i = 0; i < 5; i++) {
      const pip = this.add.graphics()
      this.comboPips.push(pip)
      this.comboContainer.add(pip)
    }

    // State label (small, below multiplier)
    this.comboStateLabel = this.add
      .text(0, 12, "BUMPER CHARGE", {
        fontSize: "8px",
        fontFamily: FONT.pixel,
        color: COLOR.whiteText,
      })
      .setOrigin(0.5)
    this.comboContainer.add(this.comboStateLabel)

    // Timer bar (below state label)
    this.comboTimerBar = this.add.graphics()
    this.comboContainer.add(this.comboTimerBar)
  }

  private redrawComboDisplay(
    state: string,
    hits: number,
    targetHits: number,
    nextEffect: string,
    hitColors: string[],
    powerMode: string,
    powerTimerPct: number,
    runState: RunStage,
  ): void {
    if (!state || runState !== "playing") {
      this.comboContainer.setVisible(false)
      this.comboPulseStrength = 0
      return
    }

    this.comboContainer.setVisible(true)
    this.comboIsPowered = state === "powered"

    const normalizedTargetHits = Math.max(1, targetHits)
    const activeHits = Phaser.Math.Clamp(hits, 0, normalizedTargetHits)
    const effectLabel = this.getSpecialLabel(nextEffect as BumperEffectType)
    const nextEffectColor = this.getComboEffectColor(
      nextEffect as BumperEffectType,
    )
    const latestHitColor = this.getLatestHitColor(hitColors, activeHits)
    const fillColor =
      state === "powered"
        ? this.getPowerModeFillColor(powerMode)
        : latestHitColor
    const borderColor = state === "powered" ? fillColor : latestHitColor

    this.comboBackdrop.clear()
    this.comboBackdrop.fillStyle(COLOR.black, 0.8)
    this.comboBackdrop.fillRect(-118, -30, 236, 62)

    // Retro border for combo
    const ctx = { x: -118, y: -30, w: 236, h: 62 }
    this.comboBackdrop.lineStyle(1, 0x000000, 1)
    this.comboBackdrop.strokeRect(ctx.x, ctx.y, ctx.w, ctx.h)
    this.comboBackdrop.lineStyle(1, borderColor, 0.6)
    this.comboBackdrop.strokeRect(ctx.x + 1, ctx.y + 1, ctx.w - 2, ctx.h - 2)

    if (state === "powered") {
      this.comboStateLabel.setText(
        powerMode ? powerMode.toUpperCase() : "POWERED",
      )
      this.comboStateLabel.setColor(this.getPowerModeTextColor(powerMode))
      this.comboPulseStrength = 0.018 + powerTimerPct * 0.02
    } else {
      this.comboStateLabel.setText(
        `${effectLabel} IN ${normalizedTargetHits - activeHits}`,
      )
      this.comboStateLabel.setColor(
        Phaser.Display.Color.IntegerToColor(latestHitColor).rgba,
      )
      this.comboPulseStrength =
        0.008 + (activeHits / normalizedTargetHits) * 0.015
    }

    this.comboTimerBar.clear()

    if (state === "powered") {
      for (const pip of this.comboPips) {
        pip.clear()
      }
      const barW = 110
      const barH = 10 // slightly taller for powered mode
      const barX = -barW / 2
      const barY = -8
      const pColor = this.getPowerModeFillColor(powerMode)

      this.comboTimerBar.fillStyle(C.panelBg, 0.6)
      this.comboTimerBar.fillRect(barX, barY, barW, barH)

      this.comboTimerBar.fillStyle(pColor, 0.95)
      this.comboTimerBar.fillRect(
        barX,
        barY,
        barW * Phaser.Math.Clamp(powerTimerPct, 0, 1),
        barH,
      )
      return
    }

    this.comboPips.forEach((pip, index) => {
      pip.clear()
      const x = -40 + index * 20
      const y = -8
      const isActive = index < activeHits
      const isTarget = index === normalizedTargetHits - 1

      const hitColorStr = hitColors?.[index] ? hitColors[index] : null
      const pipColor = hitColorStr
        ? Phaser.Display.Color.HexStringToColor(hitColorStr).color
        : nextEffectColor

      pip.fillStyle(0x000000, 1)
      pip.fillCircle(x, y, 8)
      pip.lineStyle(1, pipColor, isTarget ? 0.95 : 0.35)
      pip.strokeCircle(x, y, 8)

      if (isActive) {
        pip.fillStyle(pipColor, 1)
        pip.fillCircle(x, y, 6)
        pip.fillStyle(0xffffff, 0.5)
        pip.fillCircle(x - 2, y - 3, 2.4)
      } else {
        pip.lineStyle(1, pipColor, isTarget ? 0.8 : 0.2)
        pip.strokeCircle(x, y, 5.5)
      }
    })
  }

  private updateComboPulse(): void {
    if (!this.comboContainer) return

    if (!this.comboContainer.visible) {
      this.comboContainer.setScale(1)
      return
    }

    const frequency = this.comboIsPowered ? 82 : 126
    const pulse = Math.sin(this.time.now / frequency) * this.comboPulseStrength
    this.comboContainer.setScale(1 + pulse)
  }

  private getComboEffectColor(effect: BumperEffectType): number {
    if (effect === "charge") {
      const heroId = GameState.instance.character?.id ?? "bella"
      return getHeroColorTheme(heroId).glow
    }
    switch (effect) {
      case "guard":
        return COLOR.violet
      case "bank":
        return COLOR.orange
      case "levelup":
        return COLOR.success
      case "heal":
        return COLOR.info
      default:
        return 0xffffff
    }
  }

  private getSpecialLabel(effect: BumperEffectType): string {
    switch (effect) {
      case "charge":
        return "OVERDRIVE"
      case "guard":
        return "GUARD"
      case "bank":
        return "BANK"
      case "levelup":
        return "LEVEL UP"
      case "heal":
        return "HEAL"
      default:
        return ""
    }
  }

  private getPowerModeFillColor(powerMode: string): number {
    if (powerMode === "overdrive") {
      const heroId = GameState.instance.character?.id ?? "bella"
      return getHeroColorTheme(heroId).glow
    }
    switch (powerMode) {
      case "guard":
        return COLOR.violet
      case "bank":
        return COLOR.orange
      case "levelup":
        return COLOR.success
      default:
        return COLOR.warning
    }
  }

  private getPowerModeTextColor(powerMode: string): string {
    if (powerMode === "overdrive") {
      const heroId = GameState.instance.character?.id ?? "bella"
      return getHeroColorTheme(heroId).glowText
    }
    switch (powerMode) {
      case "guard":
        return COLOR.purpleText
      case "bank":
        return COLOR.orangeText
      case "levelup":
        return COLOR.successText
      default:
        return COLOR.warningText
    }
  }

  // ── Sector name display (below wave counter) ─────────────────

  private createSectorNameLabel(): void {
    // Position below the wave panel (top-center)
    const x = GAME_WIDTH / 2
    const y = L.margin + L.wavePanelH + 4
    this.sectorNameLabel = this.add
      .text(x, y, "", {
        fontSize: "8px",
        fontFamily: FONT.pixel,
        color: COLOR.mutedText,
      })
      .setOrigin(0.5, 0)
      .setScrollFactor(0)
      .setDepth(HUD_DEPTH)
      .setAlpha(0.6)
  }

  // ── Registry change handler ───────────────────────────────────

  private updateWarningLabel(): void {
    if (this.warningKeys.length === 0) return

    const text = `Missing: ${this.warningKeys.join(", ")}`
    if (!this.warningLabel) {
      this.warningLabel = this.add
        .text(GAME_WIDTH - L.margin, GAME_HEIGHT - L.margin, text, {
          fontSize: "8px",
          fontFamily: FONT.pixel,
          color: COLOR.magentaText,
          backgroundColor: COLOR.blackOverlayText,
          padding: { x: 4, y: 2 },
        })
        .setOrigin(1, 1)
        .setScrollFactor(0)
        .setDepth(HUD_DEPTH + 10)
    } else {
      this.warningLabel.setText(text)
    }
  }

  private createDebugCounter(): void {
    if (!window.location.search.includes("debug=1")) return

    this.debugCounterText = this.add
      .text(8, GAME_HEIGHT - 24, "", {
        fontSize: "8px",
        fontFamily: FONT.pixel,
        color: THEME.colors.whiteText,
        backgroundColor: THEME.colors.blackOverlayText,
        padding: { x: 4, y: 2 },
      })
      .setScrollFactor(0)
      .setDepth(HUD_DEPTH + 1000)
  }

  public showWaveAnnouncement(
    waveNumber: number,
    sectorName: string,
    message: string | null,
    isBossWave: boolean,
    isSectorWave: boolean,
    durationMs = 2000,
  ): void {
    const container = this.add
      .container(GAME_WIDTH / 2, GAME_HEIGHT / 2)
      .setDepth(HUD_DEPTH + 800)

    const bg = this.add.graphics()
    bg.fillStyle(COLOR.black, 0.8)
    bg.fillRect(-200, -100, 400, 200)
    bg.lineStyle(2, COLOR.warning, 1)
    bg.strokeRect(-200, -100, 400, 200)
    container.add(bg)

    container.add(
      this.add
        .text(0, -60, `SECTOR // ${sectorName.toUpperCase()}`, {
          fontSize: "8px",
          fontFamily: FONT.pixel,
          color: THEME.colors.warningText,
          letterSpacing: 2,
        })
        .setOrigin(0.5),
    )

    container.add(
      this.add
        .text(0, -30, "INVASION PHASE", {
          fontSize: "8px",
          fontFamily: FONT.pixel,
          color: THEME.colors.warningText,
        })
        .setOrigin(0.5),
    )

    container.add(
      this.add
        .text(0, 10, `WAVE ${waveNumber}`, {
          fontSize: "24px",
          fontFamily: FONT.pixel,
          color: THEME.colors.warningText,
        })
        .setOrigin(0.5),
    )

    if (message) {
      container.add(
        this.add
          .text(0, 50, `"${message}"`, {
            fontSize: "8px",
            fontFamily: FONT.pixel,
            color: THEME.colors.whiteText,
            wordWrap: { width: 340 },
            align: "center",
          })
          .setOrigin(0.5),
      )
    }

    if (isBossWave || isSectorWave) {
      const isFinalBossWave = isBossWave && waveNumber === MAX_WAVES
      container.add(
        this.add
          .text(
            0,
            80,
            isFinalBossWave
              ? "FINAL BOSS"
              : isBossWave
                ? "BOSS"
                : "SECTOR ADVANCE",
            {
              fontSize: "8px",
              fontFamily: FONT.pixel,
              color: isFinalBossWave
                ? THEME.colors.warningText
                : isBossWave
                  ? THEME.colors.dangerText
                  : THEME.colors.warningText,
            },
          )
          .setOrigin(0.5),
      )
    }

    container.setScale(0.8).setAlpha(0)
    this.tweens.add({
      targets: container,
      scale: 1,
      alpha: 1,
      duration: 400,
      ease: "Cubic.easeOut",
      onComplete: () => {
        this.time.delayedCall(durationMs, () => {
          this.tweens.add({
            targets: container,
            alpha: 0,
            scale: 0.8,
            duration: 300,
            onComplete: () => container.destroy(),
          })
        })
      },
    })
  }

  private updateDebugCounter(): void {
    if (!this.debugCounterText) return

    const balls = (this.game.registry.get("ballActiveCount") as number) ?? 0
    const projs =
      (this.game.registry.get("projectileActiveCount") as number) ?? 0
    const comboHits = (this.game.registry.get("comboHits") as number) ?? 0
    const comboTargetHits =
      (this.game.registry.get("comboTargetHits") as number) ?? 0
    const combo = `${comboHits}/${comboTargetHits}`
    const powerMode = (this.game.registry.get("powerMode") as string) ?? ""
    this.debugCounterText.setText(
      `B:${balls} P:${projs} C:${combo}${powerMode ? ` ${powerMode.slice(0, 3)}` : ""}`,
    )
  }

  private onRegistryChange(): void {
    const snapshot = this.readRegistrySnapshot()
    this.syncHealth(snapshot)
    this.syncProgress(snapshot)
    this.syncCoins(snapshot)
    this.syncScore(snapshot)
    this.syncWave(snapshot)
    this.syncRunState(snapshot)
    this.syncCombo(snapshot)
    this.syncSector(snapshot)
    this.syncBossBar(snapshot)
  }

  private readRegistrySnapshot(): {
    hp: number
    maxHp: number
    level: number
    exp: number
    expToLevel: number
    coins: number
    score: number
    wave: number
    waveTimer: number
    isBossWave: boolean
    comboState: string
    comboHits: number
    comboTargetHits: number
    comboNextEffect: string
    comboHitColors: string[]
    powerMode: string
    powerTimerPct: number
    runState: RunStage
    sectorName: string
    bossName: string
    ballActiveCount: number
    ballColor: number
    bossHp: number
    bossMaxHp: number
    bossPartCount: number
    bossType: string
    bossParts: BossPartSnapshot[]
    isBossMainBarVisible: boolean
  } {
    const reg = this.game.registry
    const powerMode = (reg.get("powerMode") as string | null) ?? ""
    const powerTimerPct = (reg.get("powerTimerPct") as number) ?? 0
    const bossWave = (reg.get("isBossWave") as boolean) ?? false

    return {
      hp: (reg.get("hp") as number) ?? 0,
      maxHp: (reg.get("maxHp") as number) ?? 100,
      level: (reg.get("level") as number) ?? 1,
      exp: (reg.get("exp") as number) ?? 0,
      expToLevel: (reg.get("expToLevel") as number) ?? 50,
      coins: (reg.get("coins") as number) ?? 0,
      score: (reg.get("score") as number) ?? 0,
      wave: (reg.get("currentWave") as number) ?? 1,
      waveTimer: (reg.get("waveTimer") as number) ?? 0,
      isBossWave: bossWave,
      comboState: (reg.get("comboState") as string) ?? "",
      comboHits: (reg.get("comboHits") as number) ?? 0,
      comboTargetHits: (reg.get("comboTargetHits") as number) ?? 5,
      comboNextEffect: (reg.get("comboNextEffect") as string) ?? "charge",
      comboHitColors: (reg.get("comboHitColors") as string[]) ?? [],
      powerMode,
      powerTimerPct,
      runState: (reg.get("runState") as RunStage) ?? "menu",
      sectorName: bossWave
        ? "BOSS BLOCKADE"
        : ((reg.get("sectorName") as string) ?? ""),
      bossName: (reg.get("bossName") as string) ?? "",
      ballActiveCount: (reg.get("ballActiveCount") as number) ?? 0,
      ballColor: (reg.get("ballColor") as number) ?? 0xf97316,
      bossHp: (reg.get("bossHp") as number) ?? 0,
      bossMaxHp: (reg.get("bossMaxHp") as number) ?? 0,
      bossPartCount: (reg.get("bossPartCount") as number) ?? 0,
      bossType: (reg.get("bossType") as string) ?? "",
      bossParts: (reg.get("bossParts") as BossPartSnapshot[]) ?? [],
      isBossMainBarVisible:
        (reg.get("isBossMainBarVisible") as boolean) ?? true,
    }
  }

  private syncHealth(snapshot: { hp: number; maxHp: number }): void {
    if (snapshot.hp === this.prev.hp && snapshot.maxHp === this.prev.maxHp)
      return

    if (snapshot.hp < this.prev.hp && this.prev.hp > 0) {
      // HP Shake feedback
      this.tweens.add({
        targets: this.statusContainer,
        x: L.margin + (Math.random() - 0.5) * 4,
        y: L.margin + (Math.random() - 0.5) * 4,
        duration: 40,
        yoyo: true,
        repeat: 3,
        onComplete: () => {
          this.statusContainer.x = L.margin
          this.statusContainer.y = L.margin
        },
      })
    }

    this.redrawHpBar(snapshot.hp, snapshot.maxHp)
    this.prev.hp = snapshot.hp
    this.prev.maxHp = snapshot.maxHp
  }

  private syncProgress(snapshot: {
    level: number
    exp: number
    expToLevel: number
  }): void {
    if (
      snapshot.level === this.prev.level &&
      snapshot.exp === this.prev.exp &&
      snapshot.expToLevel === this.prev.expToLevel
    )
      return

    if (snapshot.level > this.prev.level && this.prev.level > 0) {
      this.tweens.add({
        targets: this.levelLabel,
        scale: 1.2,
        duration: 120,
        yoyo: true,
        ease: "Cubic.easeOut",
      })
    }

    this.levelLabel.setText(`LV.${snapshot.level}`)
    this.redrawExpBar(snapshot.exp, snapshot.expToLevel)
    this.prev.level = snapshot.level
    this.prev.exp = snapshot.exp
    this.prev.expToLevel = snapshot.expToLevel
  }

  private syncCoins(snapshot: { coins: number }): void {
    if (snapshot.coins === this.prev.coins) return

    if (snapshot.coins > this.prev.coins && this.prev.coins >= 0) {
      this.tweens.add({
        targets: this.coinsLabel,
        scale: 1.2,
        duration: 100,
        yoyo: true,
        ease: "Quad.easeOut",
      })
    }

    this.coinsLabel.setText(`${snapshot.coins}`)
    this.prev.coins = snapshot.coins
  }

  private syncScore(snapshot: { score: number }): void {
    if (snapshot.score === this.prev.score) return
    this.waveScoreLabel.setText(`SCORE ${snapshot.score.toLocaleString()}`)
    this.prev.score = snapshot.score
  }

  private syncRunState(snapshot: { runState: RunStage }): void {
    if (snapshot.runState === this.prevHudRunState) return

    const isPlaying = snapshot.runState === "playing"
    if (!isPlaying) {
      this.bossBarContainer?.setVisible(false)
    }

    this.prevHudRunState = snapshot.runState
  }

  private syncWave(snapshot: {
    wave: number
    waveTimer: number
    isBossWave: boolean
  }): void {
    const timerSec = Math.ceil(snapshot.waveTimer / 1000)
    const isFinalBossWave = snapshot.isBossWave && snapshot.wave === MAX_WAVES

    if (
      snapshot.wave !== this.prev.wave ||
      snapshot.isBossWave !== this.prev.isBossWave
    ) {
      this.waveLabel.setText(isFinalBossWave ? "BOSS" : `WAVE ${snapshot.wave}`)

      this.prev.wave = snapshot.wave
      this.prev.isBossWave = snapshot.isBossWave
    }

    if (timerSec !== this.prev.waveTimerSec) {
      const m = Math.floor(timerSec / 60)
      const s = timerSec % 60
      this.waveTimerLabel.setText(`${m}:${String(s).padStart(2, "0")}`)
      this.prev.waveTimerSec = timerSec
    }
  }

  private syncCombo(snapshot: {
    comboState: string
    comboHits: number
    comboTargetHits: number
    comboNextEffect: string
    comboHitColors: string[]
    powerMode: string
    powerTimerPct: number
    runState: RunStage
  }): void {
    if (
      snapshot.comboState === this.prev.comboState &&
      snapshot.comboHits === this.prev.comboHits &&
      snapshot.comboTargetHits === this.prev.comboTargetHits &&
      snapshot.comboNextEffect === this.prev.comboNextEffect &&
      this.areColorArraysEqual(
        snapshot.comboHitColors,
        this.prev.comboHitColors,
      ) &&
      snapshot.powerMode === this.prev.powerMode &&
      snapshot.powerTimerPct === this.prev.powerTimerPct &&
      snapshot.runState === this.prev.runState
    )
      return
    this.redrawComboDisplay(
      snapshot.comboState,
      snapshot.comboHits,
      snapshot.comboTargetHits,
      snapshot.comboNextEffect,
      snapshot.comboHitColors,
      snapshot.powerMode,
      snapshot.powerTimerPct,
      snapshot.runState,
    )
    this.prev.comboState = snapshot.comboState
    this.prev.comboHits = snapshot.comboHits
    this.prev.comboTargetHits = snapshot.comboTargetHits
    this.prev.comboNextEffect = snapshot.comboNextEffect
    this.prev.comboHitColors = [...snapshot.comboHitColors]

    // Overdrive Tap Urgency visibility
    if (this.tapUrgencyContainer) {
      const isOverdrive = snapshot.powerMode === "overdrive"
      const wasOverdrive = this.prev.powerMode === "overdrive"

      if (isOverdrive && !wasOverdrive) {
        this.tapUrgencyShowCount++
        this.tapUrgencyContainer.setVisible(true)
        this.tapUrgencyContainer.setScale(0.5)
        this.tapUrgencyContainer.setAlpha(0)
        this.tweens.add({
          targets: this.tapUrgencyContainer,
          scale: 1,
          alpha: 1,
          duration: 300,
          ease: "Back.easeOut",
        })
      } else if (!isOverdrive && wasOverdrive) {
        this.tweens.add({
          targets: this.tapUrgencyContainer,
          scale: 0.5,
          alpha: 0,
          duration: 200,
          ease: "Cubic.easeIn",
          onComplete: () => {
            this.tapUrgencyContainer?.setVisible(false)
          },
        })
      }
    }

    this.prev.powerMode = snapshot.powerMode
    this.prev.powerTimerPct = snapshot.powerTimerPct
    this.prev.runState = snapshot.runState
  }

  private getLatestHitColor(hitColors: string[], activeHits: number): number {
    const latest = hitColors[Math.max(0, activeHits - 1)]
    if (!latest) return COLOR.warning
    return Phaser.Display.Color.HexStringToColor(latest).color
  }

  private areColorArraysEqual(left: string[], right: string[]): boolean {
    if (left.length !== right.length) return false
    return left.every((value, index) => value === right[index])
  }

  private syncSector(snapshot: { sectorName: string }): void {
    if (snapshot.sectorName === this.prev.sectorName) return
    this.sectorNameLabel.setText(snapshot.sectorName.toUpperCase())
    this.prev.sectorName = snapshot.sectorName
  }

  private onShutdown(): void {
    this.unregisterHudEvents()
    this.warningKeys = []

    if (this.warningLabel) {
      this.warningLabel.destroy()
      this.warningLabel = undefined
    }
    if (this.debugCounterText) {
      this.debugCounterText.destroy()
      this.debugCounterText = undefined
    }
    this.comboPips = []

    setMusicMenuVolume(this)
    this.input.removeAllListeners()
    this.input.keyboard?.removeAllListeners()
    this.time.removeAllEvents()
    this.tweens.killAll()
  }
}
